/**
 * Pickup: a parent checks their own children out of after-school, on their phone, at the door.
 *
 * Two ways in — the "Check out for pickup" button on the dashboard's Today card, and the QR code
 * by the after-school entrance, which links here with `?via=qr`. A parent who is not signed in
 * goes through login and lands back here, because `AuthenticatedRoute` carries the path along.
 *
 * Full screen: the route sits outside the parent shell, so there is no menu, bottom nav or
 * assistant bubble to hit by mistake. A child is a button, the check-out is a button, and
 * "Dashboard" at the top is the one way out — the same place on every state of the screen.
 *
 * Built for one hand and a few seconds: every child who can be checked out is already ticked, so
 * the usual visit is a single press. The press is final — nobody confirms it — so the button names
 * who it is about to check out rather than saying "Submit", and the result is a screen big enough
 * for a teacher to read over the parent's shoulder.
 *
 * After-school only. An enrichment class ends with the child walking back into after-school, not
 * out of the door with an adult; the database function refuses anything else, so this screen
 * never offers it.
 */

import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarOff, CheckCircle2, Circle, CheckCircle, ChevronLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLabelParts, useWeekdayDateLabel } from '../config/useLabels';
import {
  useParentCheckOut,
  useParentPickupToday,
  type PickupEntry,
  type PickupTodayRecord,
} from '../hooks/useAttendance';
import { initialsFor, solidTintFor } from '../components/StudentAvatar';
import { formatTimeInBusinessTZ } from '../utils/date';
import { getTodaySessionDate } from '../utils/schedule';
import { getErrorMessage } from '../utils/errorMessage';

function clock(iso: string): string {
  return formatTimeInBusinessTZ(iso, { hour12: true });
}

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

/** "Check out Emma", "Check out Emma & Leo", "Check out 3 children". */
function checkOutButtonLabel(names: string[]): string {
  if (names.length === 0) return 'Select a child';
  if (names.length === 1) return `Check out ${firstName(names[0])}`;
  if (names.length === 2) return `Check out ${firstName(names[0])} & ${firstName(names[1])}`;
  return `Check out ${names.length} children`;
}

/**
 * One button per child, not per record. A child in two after-school programs today has two
 * records, and the parent is collecting one child: the button closes both.
 */
interface Child {
  studentId: string;
  name: string;
  /** Today's checked-in records — what a check-out closes. Empty when there is nothing to tick. */
  open: PickupTodayRecord[];
  records: PickupTodayRecord[];
}

function childrenOf(records: PickupTodayRecord[]): Child[] {
  const byStudent = new Map<string, Child>();
  for (const record of records) {
    let child = byStudent.get(record.studentId);
    if (!child) {
      child = { studentId: record.studentId, name: record.studentName, open: [], records: [] };
      byStudent.set(record.studentId, child);
    }
    child.records.push(record);
    if (record.status === 'checked_in') child.open.push(record);
  }
  return [...byStudent.values()];
}

/** What a child's button says when there is nothing to tick. */
function unavailableReason(records: PickupTodayRecord[]): string {
  // A parent's own check-out is the one we can stand behind, so it is the one that names
  // somebody. A staff check-out is stated without a name or a time, as on the dashboard.
  const out = records.find(r => r.status === 'picked_up' && r.checkedOutBy === 'parent')
    ?? records.find(r => r.status === 'picked_up');
  if (out) {
    return out.checkedOutBy === 'parent' && out.pickedUpAt
      ? `Checked out ${clock(out.pickedUpAt)}${out.pickedUpByName ? ` · ${out.pickedUpByName}` : ''}`
      : 'Already checked out';
  }
  if (records.some(r => r.status === 'absent')) return 'Absent today';
  return 'Not checked in yet';
}

interface Done {
  names: string[];
  at: string;
  /** Selected but not closed — collected by someone else a moment earlier, usually. */
  skipped: string[];
}

/**
 * The whole viewport, less the safe-area padding `body` already carries, so the check-out
 * button sits at the bottom under the thumb. `min-h-screen` stands in where `dvh` is unknown.
 */
function Screen({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex min-h-screen flex-col bg-white"
      style={{ minHeight: 'calc(100dvh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-4 pb-4">
        <Link
          to="/dashboard"
          className="-ml-2 flex h-12 w-fit items-center gap-0.5 px-2 text-base font-medium text-gray-600"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          Dashboard
        </Link>
        {children}
      </div>
    </div>
  );
}

export default function Pickup() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const via: PickupEntry = params.get('via') === 'qr' ? 'qr' : 'app';
  const label = useLabelParts();
  const weekdayDate = useWeekdayDateLabel();

  const query = useParentPickupToday(user?.id);
  const checkOut = useParentCheckOut(user?.id);
  // Everyone who can be checked out starts ticked, so this holds the exceptions, by student. A
  // child who becomes available while the screen is open (checked in late) arrives ticked too.
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const [done, setDone] = useState<Done | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <Screen>
        <div className="flex flex-1 flex-col items-center justify-center pb-12 text-center">
          <CheckCircle2 className="h-24 w-24 text-green-600" aria-hidden="true" />
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Checked out</h1>
          <ul className="mt-4 space-y-1">
            {done.names.map(name => (
              <li key={name} className="text-2xl font-semibold text-gray-900">{name}</li>
            ))}
          </ul>
          <p className="mt-3 text-lg tabular-nums text-gray-600">
            {clock(done.at)} · {weekdayDate(getTodaySessionDate())}
          </p>
          {done.skipped.length > 0 && (
            <p className="mt-6 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Not checked out: {done.skipped.join(', ')}
            </p>
          )}
        </div>
      </Screen>
    );
  }

  const heading = label('header.pickup');
  // `?? []` rather than a default: a disabled query's data is undefined, a failed one keeps null.
  const kids = childrenOf(query.data ?? []);
  const available = kids.filter(k => k.open.length > 0);
  const selected = available.filter(k => !unticked.has(k.studentId));

  function toggle(studentId: string) {
    setError(null);
    setUnticked(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }

  async function handleCheckOut() {
    if (selected.length === 0 || checkOut.isPending) return;
    setError(null);
    try {
      const { closed } = await checkOut.mutateAsync({ records: selected.flatMap(k => k.open), via });
      if (closed.length === 0) {
        setError('Nobody was checked out. The list has been updated.');
        return;
      }
      const closedIds = new Set(closed.map(c => c.recordId));
      const wasClosed = (k: Child) => k.open.some(r => closedIds.has(r.recordId));
      setDone({
        names: selected.filter(wasClosed).map(k => k.name),
        at: closed[0].pickedUpAt,
        skipped: selected.filter(k => !wasClosed(k)).map(k => k.name),
      });
    } catch (e) {
      setError(getErrorMessage(e, 'Could not check out. Please try again or ask a teacher.'));
    }
  }

  const loading = query.isLoading;

  return (
    <Screen>
      <h1 className="mb-4 mt-2 text-3xl font-bold text-gray-900">
        {heading.zh && <span className="mr-2 font-normal">{heading.zh}</span>}
        {heading.en}
      </h1>

      {loading ? (
        <div className="space-y-3" aria-busy="true">
          {[1, 2].map(i => <div key={i} className="h-[88px] animate-pulse rounded-2xl bg-gray-100" />)}
        </div>
      ) : query.error ? (
        <div className="rounded-2xl bg-red-50 px-4 py-4 text-base text-red-700">
          {getErrorMessage(query.error, 'Could not load today’s attendance.')}
          <button onClick={() => void query.refetch()} className="mt-2 block font-semibold underline">
            Try again
          </button>
        </div>
      ) : kids.length === 0 ? (
        // No after-school record for any of the children today. Whatever the reason — a weekend,
        // a closure, a day the family did not book, no after-school at all — it is the same
        // thing to a parent at the door: nobody to check out because nobody was checked in. The
        // roster creates the day's rows when a teacher opens it, long before pickup.
        <div className="flex flex-1 flex-col items-center justify-center pb-16 text-center">
          <span className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-50 text-blue-400" aria-hidden="true">
            <CalendarOff className="h-12 w-12" />
          </span>
          <p className="mt-4 text-xl font-semibold text-gray-900">No after-school check-in today</p>
        </div>
      ) : (
        <>
          <ul className="flex-1 space-y-3">
            {kids.map(kid => {
              const isAvailable = kid.open.length > 0;
              const isTicked = isAvailable && !unticked.has(kid.studentId);
              const avatar = (
                <span
                  className={`flex h-14 w-14 flex-none items-center justify-center rounded-full text-lg font-bold text-white ${solidTintFor(kid.name)} ${isAvailable ? '' : 'opacity-50'}`}
                  aria-hidden="true"
                >
                  {initialsFor(kid.name)}
                </span>
              );

              if (!isAvailable) {
                return (
                  <li
                    key={kid.studentId}
                    className="flex min-h-[88px] items-center gap-4 rounded-2xl bg-gray-50 px-4 py-3"
                  >
                    {avatar}
                    <span className="min-w-0 flex-1">
                      <span className="block text-xl font-semibold text-gray-500">{kid.name}</span>
                      <span className="block text-sm text-gray-500">{unavailableReason(kid.records)}</span>
                    </span>
                    {kid.records.some(r => r.status === 'picked_up') && (
                      <CheckCircle className="h-7 w-7 flex-none text-blue-500" aria-hidden="true" />
                    )}
                  </li>
                );
              }

              // The whole row is the target, not the box: a parent juggling a bag and a sibling
              // should not have to hit a 24px square.
              return (
                <li key={kid.studentId}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isTicked}
                    onClick={() => toggle(kid.studentId)}
                    className={`flex min-h-[88px] w-full items-center gap-4 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${
                      isTicked ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    {avatar}
                    <span className="min-w-0 flex-1 text-xl font-semibold text-gray-900">{kid.name}</span>
                    {isTicked
                      ? <CheckCircle2 className="h-8 w-8 flex-none text-blue-600" aria-hidden="true" />
                      : <Circle className="h-8 w-8 flex-none text-gray-300" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>

          {error && (
            <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>
          )}

          {available.length > 0 && (
            <button
              type="button"
              onClick={() => void handleCheckOut()}
              disabled={selected.length === 0 || checkOut.isPending}
              className="mt-4 flex h-16 w-full flex-none items-center justify-center rounded-2xl bg-blue-600 text-lg font-semibold text-white transition-opacity disabled:opacity-40"
            >
              {checkOut.isPending ? 'Checking out…' : checkOutButtonLabel(selected.map(k => k.name))}
            </button>
          )}
        </>
      )}
    </Screen>
  );
}
